import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Contact.css';

/* ────────────────────────────────────────────────────────────────
   The form sends straight to WhatsApp — no backend. Submitting opens
   wa.me with the enquiry pre-typed; the visitor just taps Send.
   Every message is stamped with the website it came from so the team
   can tell website leads apart from other WhatsApp chats.
   ──────────────────────────────────────────────────────────────── */
const WHATSAPP_LINES = [
  { id: 'line1', number: '918341085415', label: '+91 83410 85415' },
];
const SITE_NAME = 'aaligroup.co';
const WA_GREETING = `Hi AALI GROUP, I found you on the ${SITE_NAME} website.`;

const waLink = (number, text) =>
  `https://wa.me/${number}?text=${encodeURIComponent(text)}`;

/* Builds the pre-typed WhatsApp message. The first line names the
   website so the source is obvious in the chat list preview. */
function buildMessage(form, route) {
  const lines = [
    `*New enquiry from ${SITE_NAME} website*`,
    `Website: https://${SITE_NAME}/contact`,
    '',
    `*Business:* ${route.name} — ${route.org}`,
    `*Name:* ${form.name.trim()}`,
    `*Email:* ${form.email.trim()}`,
    `*Phone:* ${form.phone.trim()}`,
  ];
  if (form.company.trim()) lines.push(`*Company:* ${form.company.trim()}`);
  lines.push('', '*Message:*', form.message.trim());
  return lines.join('\n');
}

const GROUP_EMAIL = 'info@aaligroup.co';
const TECH_EMAIL = 'info@aalitech.co';
const PHONE = '+918341085415';
const MAPS_URL = 'https://maps.app.goo.gl/H4Eb3U6G6xTJqs5s8';

/* Which house of the group handles what. The chosen business travels
   with the WhatsApp message so the right team picks it up. */
const ROUTES = [
  { id: 'technology', name: 'Technology & software', org: 'AALITECH',            icon: 'fa-code', sla: '48 hrs' },
  { id: 'consulting', name: 'Consulting & BPO',      org: 'AALI CONSSULTANCY',   icon: 'fa-headset', sla: '48 hrs' },
  { id: 'builders',   name: 'Builders & infra',      org: 'AALI BUILDERS',       icon: 'fa-helmet-safety', sla: '48 hrs' },
  { id: 'media',      name: 'Media & entertainment', org: 'MOVIES · FILMZ · MUSIC', icon: 'fa-clapperboard', sla: '48 hrs' },
  { id: 'luxury',     name: 'Luxury & jewellery',    org: 'AALI GOLD JEWELZ',    icon: 'fa-gem', sla: '48 hrs' },
  { id: 'social',     name: 'Social impact',         org: 'AALI TRUSTZ',         icon: 'fa-hand-holding-heart', sla: '48 hrs' },
  { id: 'careers',    name: 'Careers & hiring',      org: 'PEOPLE TEAM',         icon: 'fa-user-tie', sla: '5 working days' },
  { id: 'other',      name: 'Something else',        org: 'FRONT DESK',          icon: 'fa-circle-question', sla: '48 hrs' },
];

/* The three ways to reach us without the form */
const CHANNELS = [
  {
    icon: 'fas fa-mobile-screen-button',
    color: '#FF6B35',
    label: 'Call',
    value: '+91 83410 85415',
    landline: '+91 863 461 1770',
    note: 'Mon – Sat, 9 AM – 7 PM',
    href: `tel:${PHONE}`,
    action: 'Call now',
  },
  {
    icon: 'fas fa-location-dot',
    color: '#3B82F6',
    label: 'Visit',
    value: 'AALI GROUP, Syamala Nagar 4th Line',
    note: 'Guntur, Andhra Pradesh 522006',
    href: MAPS_URL,
    action: 'Open in Maps',
    external: true,
  },
  {
    icon: 'fas fa-envelope',
    color: '#10B981',
    label: 'Write',
    value: GROUP_EMAIL,
    note: `Technology enquiries — ${TECH_EMAIL}`,
    href: `mailto:${GROUP_EMAIL}`,
    action: 'Send an email',
  },
];

const MESSAGE_MAX = 1200;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+()\d][\d\s\-()]{7,19}$/;

const EMPTY_FORM = {
  route: 'technology',
  name: '',
  email: '',
  phone: '',
  company: '',
  message: '',
  website: '', // honeypot — humans never see it, bots fill it
};

function validate(form) {
  const errors = {};
  if (form.name.trim().length < 2) {
    errors.name = 'Enter your full name.';
  }
  if (!EMAIL_RE.test(form.email.trim())) {
    errors.email = 'Enter an email we can reply to.';
  }
  if (!PHONE_RE.test(form.phone.trim())) {
    errors.phone = 'Enter a phone number with country code.';
  }
  if (form.message.trim().length < 15) {
    errors.message = 'Tell us a little more — 15 characters minimum.';
  }
  return errors;
}

const Contact = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sent
  const [receipt, setReceipt] = useState(null);
  const panelRef = useRef(null);

  /* Same reveal-on-scroll behaviour the other pages use */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const route = useMemo(
    () => ROUTES.find((r) => r.id === form.route) || ROUTES[0],
    [form.route]
  );

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      // Errors render on the next paint, so pick the target from `found`
      // rather than querying the DOM for a class that is not there yet.
      const firstInvalid = ['name', 'email', 'phone', 'message'].find((f) => found[f]);
      const el = document.getElementById(`ct-${firstInvalid}`);
      if (el) el.focus();
      return;
    }

    // Every enquiry goes to the single WhatsApp line
    const submitter = event.nativeEvent && event.nativeEvent.submitter;
    const line =
      WHATSAPP_LINES.find((l) => submitter && l.id === submitter.value) ||
      WHATSAPP_LINES[0];

    const url = waLink(line.number, buildMessage(form, route));

    // Honeypot filled → a bot; show the success screen but open nothing
    if (!form.website) {
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) window.location.href = url; // popup blocked → same tab
    }

    setReceipt({
      line: line.label,
      url,
      org: route.org,
      sla: route.sla,
    });
    setStatus('sent');
    setForm(EMPTY_FORM);
    setErrors({});
    if (panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const startAnother = () => {
    setReceipt(null);
    setStatus('idle');
  };

  const fieldClass = (field) => `ct-field${errors[field] ? ' ct-invalid' : ''}`;

  return (
    <div className="ct-root">
      {/* ══════════ HERO ══════════ */}
      <section className="ct-hero">
        <div className="hero-overlay"></div>
        <div className="container ct-hero-inner">
          <div className="ct-hero-badge">Contact Us</div>
          <h1 className="ct-hero-title">
            Let's Build <span>Together</span>
          </h1>
          <p className="ct-hero-sub">
            AALI GROUP runs eight businesses under one roof. Pick the one you
            need and your message lands with the team that handles it — not in a
            shared inbox waiting to be forwarded.
          </p>
          <div className="ct-hero-cta">
            <a href={`tel:${PHONE}`} className="btn-primary">
              Call +91 83410 85415
            </a>
            <a
              className="ct-btn-light"
              href={waLink(WHATSAPP_LINES[0].number, WA_GREETING)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-whatsapp" aria-hidden="true" /> WhatsApp Us
            </a>
          </div>
        </div>
        <div className="hero-wave-bottom">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,64L80,58.7C160,53,320,43,480,48C640,53,800,75,960,80C1120,85,1280,75,1360,69.3L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* ══════════ DIRECT CHANNELS ══════════ */}
      <section className="ct-channels-section">
        <div className="container">
          <div className="section-header-center reveal-on-scroll">
            <span className="section-tag">Reach Us</span>
            <h2>Three Ways to Get in Touch</h2>
            <p>Call the front desk, visit the head office, or write to us directly.</p>
          </div>

          <div className="ct-channel-grid">
            {CHANNELS.map((channel) => (
              <div className="ct-channel-card reveal-on-scroll" key={channel.label}>
                <div
                  className="ct-channel-icon"
                  style={{ background: `${channel.color}15`, color: channel.color }}
                >
                  <i className={channel.icon} aria-hidden="true"></i>
                </div>
                <h3>{channel.label}</h3>
                <p className="ct-channel-value">{channel.value}</p>
                {channel.landline && (
                  <p className="ct-channel-note">
                    <i className="fas fa-phone" aria-hidden="true" /> Landline {channel.landline}
                  </p>
                )}
                <p className="ct-channel-note">{channel.note}</p>
                <a
                  className="ct-channel-link"
                  href={channel.href}
                  {...(channel.external
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                >
                  {channel.action} <i className="fas fa-arrow-right" aria-hidden="true" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ FORM + INFO RAIL ══════════ */}
      <section className="ct-board">
        <div className="container">
          <div className="section-header-center reveal-on-scroll">
            <span className="section-tag">Send a Message</span>
            <h2>Tell Us What You Need</h2>
            <p>Choose a business, share a few details, and the right team replies.</p>
          </div>

          <div className="ct-board-grid">
            {/* ── Form panel ── */}
            <div className="ct-panel reveal-on-scroll" ref={panelRef}>
              {status === 'sent' && receipt ? (
                <div className="ct-done" role="status" aria-live="polite">
                  <div className="ct-done-mark" aria-hidden="true">
                    <i className="fas fa-check" />
                  </div>
                  <h2>WhatsApp is ready.</h2>
                  <p>
                    Your message to {receipt.org} is typed out in WhatsApp — tap
                    Send there to deliver it. The team replies within {receipt.sla}.
                  </p>

                  <div className="ct-receipt">
                    <div>
                      <span className="ct-receipt-key">WhatsApp</span>
                      <span className="ct-receipt-val">{receipt.line}</span>
                    </div>
                  </div>

                  <a
                    className="ct-channel-link ct-reopen"
                    href={receipt.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    WhatsApp didn't open? Tap here <i className="fas fa-arrow-right" aria-hidden="true" />
                  </a>

                  <button type="button" className="btn-primary ct-again" onClick={startAnother}>
                    Send another message →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  {/* Step 01 — routing */}
                  <div className="ct-step">
                    <span className="ct-step-no">01</span>
                    <span className="ct-step-title">Who should see this</span>
                  </div>

                  <div className="ct-field ct-select-field">
                    <label htmlFor="ct-route">Choose the business you want to reach</label>
                    <div className="ct-select-wrap">
                      <i className={`fas ${route.icon} ct-select-icon`} aria-hidden="true" />
                      <select
                        id="ct-route"
                        name="route"
                        value={form.route}
                        onChange={update('route')}
                      >
                        {ROUTES.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name} — {option.org}
                          </option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down ct-select-caret" aria-hidden="true" />
                    </div>
                  </div>

                  {/* Live routing readout */}
                  <p className="ct-readout" key={route.id} aria-live="polite">
                    <i className="fab fa-whatsapp" aria-hidden="true" />
                    <strong>WhatsApp</strong>
                    <span className="ct-sep">·</span>
                    <span>{route.org}</span>
                    <span className="ct-sep">·</span>
                    <span>replies within {route.sla}</span>
                  </p>

                  <div className="ct-rule" />

                  {/* Step 02 — details */}
                  <div className="ct-step">
                    <span className="ct-step-no">02</span>
                    <span className="ct-step-title">Your details</span>
                  </div>

                  <div className="ct-fields">
                    <div className={fieldClass('name')}>
                      <label htmlFor="ct-name">Full name</label>
                      <input
                        id="ct-name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        placeholder="Ravi Kumar"
                        value={form.name}
                        onChange={update('name')}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? 'ct-name-error' : undefined}
                      />
                      {errors.name && (
                        <span className="ct-error" id="ct-name-error">
                          {errors.name}
                        </span>
                      )}
                    </div>

                    <div className={fieldClass('email')}>
                      <label htmlFor="ct-email">Email</label>
                      <input
                        id="ct-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@company.com"
                        value={form.email}
                        onChange={update('email')}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? 'ct-email-error' : undefined}
                      />
                      {errors.email && (
                        <span className="ct-error" id="ct-email-error">
                          {errors.email}
                        </span>
                      )}
                    </div>

                    <div className={fieldClass('phone')}>
                      <label htmlFor="ct-phone">Phone</label>
                      <input
                        id="ct-phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        placeholder="+91 98765 43210"
                        value={form.phone}
                        onChange={update('phone')}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={errors.phone ? 'ct-phone-error' : undefined}
                      />
                      {errors.phone && (
                        <span className="ct-error" id="ct-phone-error">
                          {errors.phone}
                        </span>
                      )}
                    </div>

                    <div className="ct-field">
                      <label htmlFor="ct-company">
                        Company <span className="ct-optional">optional</span>
                      </label>
                      <input
                        id="ct-company"
                        name="company"
                        type="text"
                        autoComplete="organization"
                        placeholder="Where you work"
                        value={form.company}
                        onChange={update('company')}
                      />
                    </div>

                    <div className={`${fieldClass('message')} ct-field-full`}>
                      <label htmlFor="ct-message">What do you need</label>
                      <textarea
                        id="ct-message"
                        name="message"
                        maxLength={MESSAGE_MAX}
                        placeholder="A few lines on what you are looking for, and any timeline you are working to."
                        value={form.message}
                        onChange={update('message')}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? 'ct-message-error' : undefined}
                      />
                      {errors.message ? (
                        <span className="ct-error" id="ct-message-error">
                          {errors.message}
                        </span>
                      ) : (
                        <span className="ct-count">
                          {form.message.length} / {MESSAGE_MAX}
                        </span>
                      )}
                    </div>

                    {/* Honeypot */}
                    <div className="ct-hp" aria-hidden="true">
                      <label htmlFor="ct-website">Website</label>
                      <input
                        id="ct-website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={update('website')}
                      />
                    </div>
                  </div>

                  <div className="ct-submit-row">
                    {WHATSAPP_LINES.map((line) => (
                      <button
                        key={line.id}
                        type="submit"
                        name="line"
                        value={line.id}
                        className="btn-primary ct-send"
                      >
                        <i className="fab fa-whatsapp" aria-hidden="true" />
                        Submit
                      </button>
                    ))}
                    <p className="ct-privacy">
                      Opens WhatsApp with your message typed out. Used only to
                      answer you — we do not sell or share contact details.
                    </p>
                  </div>
                </form>
              )}
            </div>

            {/* ── Info rail ── */}
            <aside className="ct-rail reveal-on-scroll">
              <div className="ct-card">
                <h3 className="ct-card-title">
                  <i className="fas fa-clock" aria-hidden="true" /> Office Hours
                </h3>
                <ul className="ct-hours">
                  <li>
                    <span>Monday – Saturday</span>
                    <strong>9:00 AM – 7:00 PM</strong>
                  </li>
                  <li>
                    <span>Sunday &amp; public holidays</span>
                    <strong className="ct-closed">Closed</strong>
                  </li>
                </ul>
              </div>

              <div className="ct-card">
                <h3 className="ct-card-title">
                  <i className="fas fa-building" aria-hidden="true" /> Head Office
                </h3>
                <p className="ct-address">
                  AALI GROUP
                  <br />
                  Syamala Nagar 4th Line
                  <br />
                  Guntur, Andhra Pradesh 522006
                  <br />
                  India
                </p>
                <a
                  className="ct-channel-link"
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get directions <i className="fas fa-arrow-right" aria-hidden="true" />
                </a>
              </div>

              <a
                className="ct-wa"
                href={waLink(WHATSAPP_LINES[0].number, WA_GREETING)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-whatsapp" aria-hidden="true" />
                <span className="ct-wa-text">
                  <strong>Message us on WhatsApp</strong>
                  <span>Fastest during office hours</span>
                </span>
              </a>

              <div className="ct-card">
                <h3 className="ct-card-title">
                  <i className="fas fa-share-nodes" aria-hidden="true" /> Follow Us
                </h3>
                {/* Instagram/Facebook open AALI CONSSULTANCY for now; YouTube and Twitter have no profile yet (no link) */}
                <div className="ct-social">
                  <a
                    href="https://www.instagram.com/aaliconssultancy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="AALI CONSSULTANCY on Instagram"
                  >
                    <i className="fab fa-instagram" aria-hidden="true" />
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61585917585366"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="AALI CONSSULTANCY on Facebook"
                  >
                    <i className="fab fa-facebook-f" aria-hidden="true" />
                  </a>
                  <a aria-hidden="true" style={{ cursor: 'default' }}>
                    <i className="fab fa-youtube" />
                  </a>
                  <a aria-hidden="true" style={{ cursor: 'default' }}>
                    <i className="fab fa-twitter" />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
